import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import { showToast } from "../helper/notifications";
import { Icon } from "@iconify/react";
import { GoogleMap, MarkerF } from "@react-google-maps/api";
import { useGoogleMaps } from "../context/GoogleMapsContext";
import PageHeader from "../components/common/PageHeader";
import LocationHistoryModal from "../components/LocationHistoryModal";
import FloatingFormActions from "../components/common/FloatingFormActions";
import CreateOrLinkMasterLocationModal from "../components/CreateOrLinkMasterLocationModal";

const DEFAULT_CENTER = { lat: 47.8238, lng: 13.0288 };

const TABS = [
  { id: "Identity", icon: "solar:shop-2-linear" },
  { id: "Address", icon: "solar:map-point-linear" },
  { id: "Opening Hours", icon: "solar:clock-circle-linear" },
  { id: "Categories & Details", icon: "solar:tag-horizontal-linear" },
  { id: "Media", icon: "solar:gallery-wide-linear" },
  { id: "Raw Payload", icon: "solar:code-square-linear" },
];

// Helper function to safely parse Google Business opening hours
const formatGoogleHours = (regularHours) => {
  const formatted = { 
    monday: "Closed", 
    tuesday: "Closed", 
    wednesday: "Closed", 
    thursday: "Closed", 
    friday: "Closed", 
    saturday: "Closed", 
    sunday: "Closed" 
  };

  if (!regularHours) return formatted;

  const periods = regularHours.periods || (Array.isArray(regularHours) ? regularHours : []);

  const parseTime = (timeVal) => {
    if (!timeVal) return "00:00";
    
    if (typeof timeVal === "object") {
      const h = String(timeVal.hours || 0).padStart(2, "0");
      const m = String(timeVal.minutes || 0).padStart(2, "0");
      return `${h}:${m}`;
    }

    const str = String(timeVal);
    if (str.includes(":")) return str;
    
    const padded = str.padStart(4, "0");
    return `${padded.slice(0, 2)}:${padded.slice(2, 4)}`;
  };

  periods.forEach((p) => {
    const dayStr = (p.openDay || p.day || "").toLowerCase();
    if (dayStr && formatted.hasOwnProperty(dayStr)) {
      const open = parseTime(p.openTime);
      const close = parseTime(p.closeTime);
      formatted[dayStr] = `${open} - ${close}`;
    }
  });

  return formatted;
};

const LocationsGoogleBusinessFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoaded } = useGoogleMaps();

  const [activeTab, setActiveTab] = useState("Identity");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locationData, setLocationData] = useState(null);
  const [mediaFilter, setMediaFilter] = useState("ALL");
  const [previewItem, setPreviewItem] = useState(null);

  // Editable Form State
  const [formData, setFormData] = useState({
    title: "",
    store_code: "",
    primary_phone: "",
    website_uri: "",
    description: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    country_code: "",
    latitude: "",
    longitude: "",
    primary_category_name: "",
    open_status: "OPEN",
  });

  const [historyEndpoint, setHistoryEndpoint] = useState(null);
  const [initialData, setInitialData] = useState(null);
  const [createLinkModalOpen, setCreateLinkModalOpen] = useState(false);

  const loadData = useCallback(async (opts = {}) => {
    if (!opts.silent) setLoading(true);
    try {
      const res = await api.get(`/google-business/locations/${id}`);
      if (res.data?.success) {
        const data = res.data.data;
        setLocationData(data);
        const formObj = {
          title: data.title || "",
          store_code: data.store_code || "",
          primary_phone: data.primary_phone || "",
          website_uri: data.website_uri || "",
          description: data.description || "",
          address_line1: data.address_line1 || "",
          address_line2: data.address_line2 || "",
          city: data.city || "",
          state: data.state || "",
          postal_code: data.postal_code || "",
          country_code: data.country_code || "",
          latitude: data.latitude || "",
          longitude: data.longitude || "",
          primary_category_name: data.primary_category_name || "",
          open_status: data.open_status || "OPEN",
        };
        setFormData(formObj);
        setInitialData(formObj);

        if (data.id && String(data.id) !== String(id)) {
          navigate(`/google-business/${data.id}/edit`, { replace: true });
        }
      }
    } catch (err) {
      showToast("error", "Failed to load Google Business Profile details");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const hasFormChanged = () => {
    if (!initialData) return false;
    return Object.keys(formData).some((k) => String(formData[k] || "") !== String(initialData[k] || ""));
  };

  const currentTabIdx = TABS.findIndex((t) => t.id === activeTab);
  const prevTab = currentTabIdx > 0 ? TABS[currentTabIdx - 1] : null;
  const nextTab = currentTabIdx >= 0 && currentTabIdx < TABS.length - 1 ? TABS[currentTabIdx + 1] : null;

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        title: formData.title,
        primaryPhone: formData.primary_phone,
        websiteUri: formData.website_uri,
        description: formData.description,
        storefrontAddress: {
          addressLines: [formData.address_line1, formData.address_line2].filter(Boolean),
          locality: formData.city,
          administrativeArea: formData.state,
          postalCode: formData.postal_code,
          regionCode: formData.country_code,
        },
      };

      const res = await api.patch(`/google-business/locations/${locationData?.id || id}`, payload);

      if (res.data?.success) {
        const newVer = res.data?.data?.versionNumber;
        showToast(
          "success",
          newVer
            ? `Google Business Profile updated and saved as version v${newVer}!`
            : "Google Business Profile updated successfully!"
        );
        await loadData({ silent: true });
      }
    } catch (err) {
      showToast("error", err.response?.data?.error || "Failed to save profile changes");
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreVersion = async (versionNumber) => {
    try {
      const targetId = locationData?.id || id;
      const res = await api.post(`/google-business/locations/${targetId}/restore/${versionNumber}`);
      if (res.data?.success) {
        showToast("success", res.data.message || `Restored version v${versionNumber} as latest!`);
        setHistoryEndpoint(null);
        await loadData({ silent: true });
      }
    } catch (err) {
      showToast("error", err.response?.data?.error || "Failed to restore version");
    }
  };

  // Previous & Next navigation handlers based on ID
  const currentId = Number(id);
  const handlePrevLocation = () => {
    if (currentId > 1) {
      navigate(`/google-business/${currentId - 1}/edit`);
    }
  };

  const handleNextLocation = () => {
    navigate(`/google-business/${currentId + 1}/edit`);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-80 bg-base radius-12 shadow-sm">
        <Icon icon="line-md:loading-twotone-loop" className="text-primary-600 text-4xl" />
      </div>
    );
  }

  if (!locationData) {
    return (
      <div className="p-32 text-center">
        <h5>Location profile not found.</h5>
        <button className="btn btn-primary-600 mt-16" onClick={() => navigate("/google-business")}>
          Back to Google Profiles
        </button>
      </div>
    );
  }

  const rawPayload = typeof locationData.raw_payload === 'string'
    ? JSON.parse(locationData.raw_payload || '{}')
    : locationData.raw_payload || {};

  const regularHours = typeof locationData.regular_hours === 'string'
    ? JSON.parse(locationData.regular_hours || '{}')
    : locationData.regular_hours || {};

  const mediaList = Array.isArray(locationData.media)
    ? locationData.media
    : Array.isArray(rawPayload.media)
      ? rawPayload.media
      : [];

  const filteredMedia =
    mediaFilter === "ALL"
      ? mediaList
      : mediaList.filter(
          (m) =>
            (m.category || "").toUpperCase() === mediaFilter ||
            (m.originalCategory || "").toUpperCase() === mediaFilter ||
            (mediaFilter === "COVER" && m.isCover)
        );

  const syncedMediaCount = mediaList.filter((m) => m.syncedToGoogle).length;

  const isConfirmed =
    typeof locationData.is_confirmed === "boolean"
      ? locationData.is_confirmed
      : ["VERIFIED", "CONFIRMED", "TRUE", "1"].includes(
          String(locationData.is_verified || "").toUpperCase()
        );

  const confirmationState =
    locationData.confirmation_status === "PENDING" ||
    String(locationData.is_verified || "").toUpperCase() === "PENDING"
      ? "PENDING"
      : isConfirmed
        ? "CONFIRMED"
        : "NOT_CONFIRMED";

  const handleUpdateConfirmation = async (nextStatus) => {
    try {
      const targetId = locationData.id || id;
      const res = await api.patch(`/google-business/locations/${targetId}/confirmation`, {
        status: nextStatus,
      });
      if (res.data?.success) {
        showToast("success", res.data.message || "Confirmation status updated");
        await fetchLocationDetail({ live: false });
      }
    } catch (err) {
      showToast("error", err.response?.data?.error || "Failed to update confirmation status");
    }
  };

  const pos = {
    lat: Number(formData.latitude) || DEFAULT_CENTER.lat,
    lng: Number(formData.longitude) || DEFAULT_CENTER.lng,
  };

  return (
    <>
      {/* Top Bar Navigation */}
      <div className="d-flex align-items-center justify-content-between mb-24 flex-wrap gap-2">
        <button
          type="button"
          onClick={() => navigate("/google-business")}
          className="btn btn-link p-0 text-secondary-light text-decoration-none d-inline-flex align-items-center gap-2 hover-text-primary-600 transition-all"
        >
          <Icon icon="solar:arrow-left-linear" className="text-xl" />
          <span className="fw-medium text-sm">Back to Google Profiles</span>
        </button>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() =>
              handleUpdateConfirmation(isConfirmed ? "NOT_CONFIRMED" : "CONFIRMED")
            }
            title={
              isConfirmed
                ? "Location is Confirmed in GBP. Click to mark as Not Confirmed."
                : "Location is Not Confirmed in GBP. Click to mark as Confirmed."
            }
            className={`badge border-0 px-12 py-8 radius-8 fw-semibold text-xs d-inline-flex align-items-center gap-1 pointer ${
              confirmationState === "CONFIRMED"
                ? "bg-success-focus text-success-main"
                : confirmationState === "PENDING"
                  ? "bg-warning-focus text-warning-main"
                  : "bg-danger-focus text-danger-main"
            }`}
          >
            <Icon
              icon={
                confirmationState === "CONFIRMED"
                  ? "solar:verified-check-bold"
                  : confirmationState === "PENDING"
                    ? "solar:clock-circle-bold"
                    : "solar:close-circle-bold"
              }
              className="text-base"
            />
            <span>
              {confirmationState === "CONFIRMED"
                ? "Confirmed"
                : confirmationState === "PENDING"
                  ? "Pending Confirmation"
                  : "Not Confirmed"}
            </span>
          </button>

          <span className="badge bg-primary-50 text-primary-600 border border-primary-200 px-12 py-8 radius-8 fw-semibold text-xs d-inline-flex align-items-center gap-1">
            <Icon icon="solar:layers-minimalistic-bold" className="text-sm" />
            <span>
              Version v{locationData.version_number || 1} (Latest
              {locationData.total_versions > 1
                ? ` of ${locationData.total_versions}`
                : ""}
              )
            </span>
          </span>

          {locationData.linked_location_id && (
            <button
              type="button"
              onClick={() => navigate(`/locations-main/${locationData.linked_location_id}/edit`)}
              className="btn btn-success-focus text-success-main d-inline-flex align-items-center gap-2 px-14 py-8 radius-8 fw-semibold text-sm border-0"
            >
              <Icon icon="solar:link-circle-bold" className="text-lg" />
              <span>Linked Master Data: {locationData.linked_location_name || `#${locationData.linked_location_id}`}</span>
            </button>
          )}

          <button
            type="button"
            onClick={() => setCreateLinkModalOpen(true)}
            className="btn btn-primary-600 d-inline-flex align-items-center gap-2 px-16 py-8 radius-8 fw-semibold text-sm"
          >
            <Icon icon="solar:map-point-add-bold" className="text-lg" />
            <span>
              {locationData.linked_location_id
                ? "Create / Re-Link Master Location"
                : "Create / Link in Master Data"}
            </span>
          </button>

          <button
            type="button"
            onClick={() =>
              setHistoryEndpoint(
                `/google-business/locations/${locationData.id || id}/history`
              )
            }
            className="btn btn-outline-primary d-inline-flex align-items-center gap-2 px-16 py-8 radius-8 fw-semibold"
          >
            <Icon icon="solar:history-linear" className="text-xl" />
            <span className="text-sm">View History</span>
          </button>
        </div>
      </div>

      <PageHeader title={`Edit Profile: ${locationData.title || "Business Location"}`} />

      <form onSubmit={handleSave} className="form-has-floating-actions">
        <div className="card radius-12 border-0 shadow-none bg-base overflow-hidden mb-24">
          <div className="d-flex flex-column flex-lg-row">
            
            {/* Sidebar Tabs */}
            <div className="bg-neutral-50 border-end" style={{ minWidth: "260px" }}>
              <div className="p-16">
                <div className="d-flex flex-column gap-1">
                  {TABS.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={`d-flex align-items-center justify-content-between gap-3 px-16 py-12 radius-8 border-0 transition-all text-start w-100 ${
                        activeTab === tab.id
                          ? "bg-primary-600 text-white shadow-sm fw-bold"
                          : "bg-transparent text-secondary-light hover-bg-neutral-200"
                      }`}
                    >
                      <span className="d-inline-flex align-items-center gap-3">
                        <Icon icon={tab.icon} className={`text-xl ${activeTab === tab.id ? "text-white" : "text-primary-600"}`} />
                        <span className="text-sm">{tab.id}</span>
                      </span>
                      {tab.id === "Media" && mediaList.length > 0 && (
                        <span
                          className={`badge radius-pill text-xs px-8 py-2 ${
                            activeTab === tab.id
                              ? "bg-white text-primary-600"
                              : "bg-primary-50 text-primary-600"
                          }`}
                        >
                          {mediaList.length}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Editable Form Body */}
            <div className="flex-grow-1 p-32 p-lg-40" style={{ minHeight: "65vh" }}>
              <h5 className="fw-bold text-primary-light mb-32 border-bottom pb-16">{activeTab}</h5>

              {activeTab === "Identity" && (
                <div className="row g-4">
                  <div className="col-12">
                    <div
                      className={`p-16 radius-10 border d-flex flex-wrap align-items-center justify-content-between gap-3 ${
                        confirmationState === "CONFIRMED"
                          ? "bg-success-focus border-success-main"
                          : confirmationState === "PENDING"
                            ? "bg-warning-focus border-warning-main"
                            : "bg-danger-focus border-danger-main"
                      }`}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <Icon
                          icon={
                            confirmationState === "CONFIRMED"
                              ? "solar:verified-check-bold"
                              : confirmationState === "PENDING"
                                ? "solar:clock-circle-bold"
                                : "solar:danger-circle-bold"
                          }
                          className={`text-2xl ${
                            confirmationState === "CONFIRMED"
                              ? "text-success-main"
                              : confirmationState === "PENDING"
                                ? "text-warning-main"
                                : "text-danger-main"
                          }`}
                        />
                        <div>
                          <div className="fw-bold text-sm">
                            Google Business Profile Confirmation:{" "}
                            <span
                              className={
                                confirmationState === "CONFIRMED"
                                  ? "text-success-main"
                                  : confirmationState === "PENDING"
                                    ? "text-warning-main"
                                    : "text-danger-main"
                              }
                            >
                              {confirmationState === "CONFIRMED"
                                ? "Confirmed (Verified)"
                                : confirmationState === "PENDING"
                                  ? "Pending Confirmation"
                                  : "Not Confirmed (Unverified)"}
                            </span>
                          </div>
                          <div className="text-xs text-secondary-light">
                            {confirmationState === "CONFIRMED"
                              ? "This location is marked as confirmed in Google Business Profile."
                              : "This location is not yet confirmed in Google Business Profile."}
                          </div>
                        </div>
                      </div>

                      <div className="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleUpdateConfirmation("CONFIRMED")}
                          className={`btn btn-xs radius-6 px-12 py-6 text-xs fw-semibold d-inline-flex align-items-center gap-1 ${
                            confirmationState === "CONFIRMED"
                              ? "btn-success text-white"
                              : "btn-outline-success bg-white"
                          }`}
                        >
                          <Icon icon="solar:verified-check-bold" />
                          <span>Mark Confirmed</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleUpdateConfirmation("NOT_CONFIRMED")}
                          className={`btn btn-xs radius-6 px-12 py-6 text-xs fw-semibold d-inline-flex align-items-center gap-1 ${
                            confirmationState === "NOT_CONFIRMED"
                              ? "btn-danger text-white"
                              : "btn-outline-danger bg-white"
                          }`}
                        >
                          <Icon icon="solar:close-circle-bold" />
                          <span>Mark Not Confirmed</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label text-xs fw-bold text-secondary-light">BUSINESS TITLE</label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      value={formData.title}
                      onChange={(e) => handleChange("title", e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-xs fw-bold text-secondary-light">STORE CODE</label>
                    <input
                      type="text"
                      className="form-control bg-neutral-100 radius-8"
                      value={formData.store_code}
                      readOnly
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-xs fw-bold text-secondary-light">PRIMARY PHONE</label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      value={formData.primary_phone}
                      onChange={(e) => handleChange("primary_phone", e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-xs fw-bold text-secondary-light">WEBSITE URI</label>
                    <input
                      type="url"
                      className="form-control radius-8"
                      value={formData.website_uri}
                      onChange={(e) => handleChange("website_uri", e.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label text-xs fw-bold text-secondary-light">DESCRIPTION</label>
                    <textarea
                      className="form-control radius-8"
                      rows="4"
                      value={formData.description}
                      onChange={(e) => handleChange("description", e.target.value)}
                    />
                  </div>
                </div>
              )}

              {activeTab === "Address" && (
                <div className="row g-4">
                  <div className="col-lg-5">
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="form-label text-xs fw-bold text-secondary-light">STREET ADDRESS 1</label>
                        <input
                          type="text"
                          className="form-control form-control-sm radius-8"
                          value={formData.address_line1}
                          onChange={(e) => handleChange("address_line1", e.target.value)}
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label text-xs fw-bold text-secondary-light">STREET ADDRESS 2</label>
                        <input
                          type="text"
                          className="form-control form-control-sm radius-8"
                          value={formData.address_line2}
                          onChange={(e) => handleChange("address_line2", e.target.value)}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label text-xs fw-bold text-secondary-light">CITY</label>
                        <input
                          type="text"
                          className="form-control form-control-sm radius-8"
                          value={formData.city}
                          onChange={(e) => handleChange("city", e.target.value)}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label text-xs fw-bold text-secondary-light">STATE</label>
                        <input
                          type="text"
                          className="form-control form-control-sm radius-8"
                          value={formData.state}
                          onChange={(e) => handleChange("state", e.target.value)}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label text-xs fw-bold text-secondary-light">POSTAL CODE</label>
                        <input
                          type="text"
                          className="form-control form-control-sm radius-8"
                          value={formData.postal_code}
                          onChange={(e) => handleChange("postal_code", e.target.value)}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label text-xs fw-bold text-secondary-light">COUNTRY CODE</label>
                        <input
                          type="text"
                          className="form-control form-control-sm radius-8"
                          value={formData.country_code}
                          onChange={(e) => handleChange("country_code", e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-7">
                    <div className="border radius-12 overflow-hidden shadow-sm h-100" style={{ minHeight: "350px" }}>
                      {isLoaded ? (
                        <GoogleMap
                          mapContainerStyle={{ width: "100%", height: "100%" }}
                          center={pos}
                          zoom={15}
                          options={{ mapTypeControl: true }}
                        >
                          <MarkerF position={pos} />
                        </GoogleMap>
                      ) : (
                        <div className="d-flex align-items-center justify-content-center h-100 bg-neutral-100 text-secondary-light">
                          Loading Map Preview...
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "Opening Hours" && (() => {
                const parsedHours = formatGoogleHours(regularHours);
                return (
                  <div className="bg-neutral-50 p-24 radius-12 border">
                    <div className="row g-3">
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                        <div className="col-md-6 d-flex align-items-center gap-3 bg-white p-12 radius-8 border shadow-sm" key={day}>
                          <label className="fw-bold text-sm text-primary-600 mb-0 text-capitalize" style={{ width: "100px" }}>
                            {day}
                          </label>
                          <input
                            className="form-control border-0 bg-neutral-50 text-sm fw-medium"
                            readOnly
                            value={parsedHours[day] || "Closed"}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {activeTab === "Categories & Details" && (
                <div className="row g-4">
                  <div className="col-md-6">
                    <label className="form-label text-xs fw-bold text-secondary-light">PRIMARY CATEGORY</label>
                    <input
                      className="form-control bg-neutral-100 radius-8"
                      readOnly
                      value={formData.primary_category_name || "—"}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-xs fw-bold text-secondary-light">OPEN STATUS</label>
                    <input
                      className="form-control bg-neutral-100 radius-8"
                      readOnly
                      value={formData.open_status || "OPEN"}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label text-xs fw-bold text-secondary-light">ADDITIONAL CATEGORIES</label>
                    <textarea
                      className="form-control bg-neutral-100 text-xs radius-8"
                      rows="3"
                      readOnly
                      value={locationData.additional_categories || "[]"}
                    />
                  </div>
                </div>
              )}

              {activeTab === "Media" && (
                <div>
                  {/* Summary & Linked Master Location Action */}
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 bg-neutral-50 p-16 radius-12 border mb-24">
                    <div className="d-flex flex-wrap align-items-center gap-3">
                      <span className="badge bg-primary-50 text-primary-600 px-12 py-8 radius-8 fw-semibold text-xs d-inline-flex align-items-center gap-1">
                        <Icon icon="solar:gallery-wide-bold" className="text-base" />
                        Total Media: {mediaList.length}
                      </span>
                      <span className="badge bg-success-focus text-success-main px-12 py-8 radius-8 fw-semibold text-xs d-inline-flex align-items-center gap-1">
                        <Icon icon="solar:check-circle-bold" className="text-base" />
                        Synced to Google: {syncedMediaCount}
                      </span>
                    </div>

                    {locationData.linked_location_id && (
                      <button
                        type="button"
                        onClick={() => navigate(`/locations-main/${locationData.linked_location_id}/edit`)}
                        className="btn btn-sm btn-outline-primary radius-8 px-12 py-6 fw-semibold d-inline-flex align-items-center gap-2"
                      >
                        <Icon icon="solar:upload-minimalistic-linear" className="text-base" />
                        <span>Manage Media in Master Location</span>
                      </button>
                    )}
                  </div>

                  {/* Category Filter Pills */}
                  {mediaList.length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mb-20">
                      {["ALL", "COVER", "LOGO", "EXTERIOR", "INTERIOR", "ADDITIONAL"].map((cat) => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => setMediaFilter(cat)}
                          className={`btn btn-sm radius-8 px-12 py-6 text-xs fw-semibold ${
                            mediaFilter === cat
                              ? "btn-primary-600 text-white"
                              : "btn-outline-secondary"
                          }`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Media Grid */}
                  {filteredMedia.length === 0 ? (
                    <div className="text-center py-48 bg-neutral-50 radius-12 border">
                      <Icon icon="solar:gallery-remove-linear" className="text-4xl text-secondary-light mb-12" />
                      <h6 className="fw-semibold text-secondary-light mb-8">No Media Files Found</h6>
                      <p className="text-xs text-secondary-light mb-16">
                        Upload photos or floorplans on the linked Master Location and export to Google Business Profile.
                      </p>
                      {locationData.linked_location_id && (
                        <button
                          type="button"
                          onClick={() => navigate(`/locations-main/${locationData.linked_location_id}/edit`)}
                          className="btn btn-sm btn-primary-600 radius-8 px-16 py-8"
                        >
                          Go to Master Location
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="row g-3">
                      {filteredMedia.map((item, idx) => {
                        const isVideo =
                          item.mediaType === "VIDEO" ||
                          (item.mimeType && item.mimeType.startsWith("video/"));
                        const isDoc =
                          item.mediaType === "DOCUMENT" ||
                          (item.mimeType && item.mimeType.includes("pdf"));

                        return (
                          <div className="col-sm-6 col-md-4 col-xl-3" key={item.id || idx}>
                            <div className="card h-100 radius-12 border shadow-sm overflow-hidden bg-white">
                              <div
                                className="position-relative bg-neutral-100 d-flex align-items-center justify-content-center overflow-hidden"
                                style={{ height: "170px", cursor: "pointer" }}
                                onClick={() => setPreviewItem(item)}
                              >
                                {isVideo ? (
                                  <video
                                    src={item.publicUrl}
                                    className="w-100 h-100"
                                    style={{ objectFit: "cover" }}
                                    muted
                                  />
                                ) : isDoc ? (
                                  <div className="text-center p-16">
                                    <Icon icon="solar:file-text-bold-duotone" className="text-5xl text-danger-main" />
                                    <div className="text-xs text-secondary-light mt-1">PDF / Document</div>
                                  </div>
                                ) : (
                                  <img
                                    src={item.publicUrl}
                                    alt={item.fileName || item.caption || "Location media"}
                                    className="w-100 h-100"
                                    style={{ objectFit: "cover" }}
                                  />
                                )}

                                {/* Top Badges */}
                                <div className="position-absolute top-0 start-0 p-8 d-flex flex-wrap gap-1">
                                  <span className="badge bg-dark bg-opacity-75 text-white text-2xs px-8 py-4 radius-4">
                                    {item.category || "ADDITIONAL"}
                                  </span>
                                  {item.isCover && (
                                    <span className="badge bg-warning-main text-white text-2xs px-8 py-4 radius-4">
                                      COVER
                                    </span>
                                  )}
                                </div>

                                {/* Google Sync Badge */}
                                <div className="position-absolute bottom-0 end-0 p-8">
                                  {item.syncedToGoogle ? (
                                    <span
                                      className="badge bg-success-main text-white text-2xs px-8 py-4 radius-4 d-inline-flex align-items-center gap-1"
                                      title={item.syncedAt ? `Synced at ${new Date(item.syncedAt).toLocaleString()}` : "Synced to Google Business Profile"}
                                    >
                                      <Icon icon="solar:check-circle-bold" />
                                      GBP Synced
                                    </span>
                                  ) : (
                                    <span className="badge bg-secondary text-white text-2xs px-8 py-4 radius-4">
                                      Master Media
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="p-12 d-flex flex-column justify-content-between flex-grow-1">
                                <div>
                                  <div
                                    className="fw-semibold text-xs text-primary-light text-truncate"
                                    title={item.fileName || item.originalName || ""}
                                  >
                                    {item.fileName || item.originalName || `Media #${idx + 1}`}
                                  </div>
                                  {item.caption && (
                                    <div className="text-2xs text-secondary-light text-truncate mt-1" title={item.caption}>
                                      {item.caption}
                                    </div>
                                  )}
                                </div>

                                <div className="d-flex align-items-center justify-content-between mt-10 pt-8 border-top">
                                  <span className="text-2xs text-secondary-light">
                                    {item.mediaType || "IMAGE"}
                                  </span>
                                  {item.publicUrl && (
                                    <a
                                      href={item.publicUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-primary-600 fw-semibold text-decoration-none d-inline-flex align-items-center gap-1"
                                    >
                                      <span>Open</span>
                                      <Icon icon="solar:square-top-down-linear" />
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {activeTab === "Raw Payload" && (
                <div className="bg-neutral-50 p-16 radius-12 border">
                  <pre className="font-monospace text-xs mb-0 bg-white p-16 radius-8 border" style={{ maxHeight: "400px", overflowY: "auto" }}>
                    {JSON.stringify(rawPayload, null, 2)}
                  </pre>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* STICKY BOTTOM ACTION BAR */}
        <FloatingFormActions
          isDirty={hasFormChanged()}
          prevStep={prevTab?.id}
          onPrev={prevTab ? () => setActiveTab(prevTab.id) : null}
          nextStep={nextTab?.id}
          onNext={nextTab ? () => setActiveTab(nextTab.id) : null}
          cancelUrl="/google-business"
          cancelText="Discard"
          // saveText="Save Changes"
          loading={saving}
          extraLeft={
            <div className="d-flex align-items-center gap-2 ms-md-2 border-start ps-md-3">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary radius-6 px-12 py-6 text-xs fw-semibold d-inline-flex align-items-center gap-1"
                onClick={handlePrevLocation}
                disabled={currentId <= 1}
              >
                <Icon icon="solar:alt-arrow-left-linear" />
                <span>Prev Location</span>
              </button>

              <button
                type="button"
                className="btn btn-sm btn-outline-secondary radius-6 px-12 py-6 text-xs fw-semibold d-inline-flex align-items-center gap-1"
                onClick={handleNextLocation}
              >
                <span>Next Location</span>
                <Icon icon="solar:alt-arrow-right-linear" />
              </button>
            </div>
          }
        />
      </form>

      {/* Media Preview Lightbox Modal */}
      {previewItem && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(15, 23, 42, 0.75)", zIndex: 1060 }}
          onClick={() => setPreviewItem(null)}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content radius-16 border-0 overflow-hidden shadow-lg">
              <div className="modal-header border-bottom px-20 py-14 d-flex align-items-center justify-content-between">
                <div className="text-truncate pe-16">
                  <h6 className="mb-0 fw-bold text-sm text-truncate">
                    {previewItem.fileName || previewItem.originalName || "Media Preview"}
                  </h6>
                  <span className="text-xs text-secondary-light">
                    Category: {previewItem.category || "ADDITIONAL"}
                  </span>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setPreviewItem(null)}
                />
              </div>
              <div className="modal-body p-20 bg-neutral-900 text-center d-flex align-items-center justify-content-center" style={{ minHeight: "360px" }}>
                {previewItem.mediaType === "VIDEO" ||
                (previewItem.mimeType && previewItem.mimeType.startsWith("video/")) ? (
                  <video
                    src={previewItem.publicUrl}
                    controls
                    autoPlay
                    className="w-100 radius-8"
                    style={{ maxHeight: "70vh" }}
                  />
                ) : (
                  <img
                    src={previewItem.publicUrl}
                    alt={previewItem.fileName || "Preview"}
                    className="img-fluid radius-8"
                    style={{ maxHeight: "70vh", objectFit: "contain" }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {historyEndpoint && (
        <LocationHistoryModal
          endpoint={historyEndpoint}
          onClose={() => setHistoryEndpoint(null)}
          onRestore={handleRestoreVersion}
        />
      )}

      {/* Create or Link in Master Data Modal */}
      <CreateOrLinkMasterLocationModal
        isOpen={createLinkModalOpen}
        onClose={() => setCreateLinkModalOpen(false)}
        gbpLocation={locationData}
        onSuccess={() => {
          loadData();
        }}
      />
    </>
  );
};

export default LocationsGoogleBusinessFormPage;